import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Footer from "../layouts/Footer";
import { useAuth } from "../hooks/useAuth";
import axios from "axios";
import Select from "react-select";

import { fetchEntitesList } from "../services/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

const METHOD_CONFIG = {
  GET:    { color: '#0ea5e9', bg: '#e0f2fe', icon: 'bi-arrow-down-left',   label: 'GET' },
  POST:   { color: '#10b981', bg: '#d1fae5', icon: 'bi-arrow-up-right',    label: 'POST' },
  PUT:    { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-pencil-square',     label: 'PUT' },
  PATCH:  { color: '#8b5cf6', bg: '#ede9fe', icon: 'bi-pencil',            label: 'PATCH' },
  DELETE: { color: '#ef4444', bg: '#fee2e2', icon: 'bi-trash',             label: 'DELETE' },
};

const getStatusConfig = (code) => {
  const c = parseInt(code);
  if (c >= 200 && c < 300) return { color: '#10b981', bg: '#d1fae5', icon: 'bi-check-circle-fill', label: 'Succès' };
  if (c >= 300 && c < 400) return { color: '#0ea5e9', bg: '#e0f2fe', icon: 'bi-arrow-repeat', label: 'Redirect' };
  if (c >= 400 && c < 500) return { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-exclamation-triangle-fill', label: 'Client Error' };
  if (c >= 500)            return { color: '#ef4444', bg: '#fee2e2', icon: 'bi-x-circle-fill', label: 'Server Error' };
  return { color: '#6b7280', bg: '#f3f4f6', icon: 'bi-question-circle', label: 'Inconnu' };
};

export default function Logs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filterMethod, setFilterMethod] = useState(searchParams.get('method') || 'all');
  const [filterCode, setFilterCode] = useState(searchParams.get('code') || 'all');
  const [filterEntite, setFilterEntite] = useState(searchParams.get('entite_id') || 'all');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [dateDebut, setDateDebut] = useState(searchParams.get('start_date') || '');
  const [dateFin, setDateFin] = useState(searchParams.get('end_date') || '');

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [entites, setEntites] = useState([]);
  const [loadingEntites, setLoadingEntites] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalFromServer, setTotalFromServer] = useState(0);

  // ✅ Copie feedback
  const [copiedField, setCopiedField] = useState(null);

  const { user } = useAuth();
  const initDoneRef = useRef(false);

  // ============================================
  // CHARGEMENT DES ENTITÉS
  // ============================================
  const loadEntites = useCallback(async () => {
    try {
      setLoadingEntites(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const entitesData = await fetchEntitesList(token);

      if (Array.isArray(entitesData)) {
        const formattedEntites = entitesData.map(entite => ({
          value: String(entite.business_id),
          label: entite.business_name || `Entité #${entite.business_id}`,
          business_email: entite.business_email || '',
          business_id: entite.business_id
        }));
        setEntites(formattedEntites);
      }
    } catch (err) {
      console.error('❌ Erreur chargement entités:', err);
    } finally {
      setLoadingEntites(false);
    }
  }, []);

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    loadEntites();
  }, [loadEntites]);

  // ============================================
  // CHARGEMENT DE LA LISTE
  // ============================================
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
      };
      if (dateDebut) params.start_date = dateDebut;
      if (dateFin) params.end_date = dateFin;
      if (filterMethod !== 'all') params.method = filterMethod;
      if (filterCode !== 'all') params.response_code = filterCode;
      if (filterEntite && filterEntite !== 'all') params.entite_id = filterEntite;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await apiClient.get('/logs', { params });
      const result = response.data;

      let logsData = [];
      let totalCount = 0;

      if (result?.data && Array.isArray(result.data)) {
        logsData = result.data;
        totalCount = result.total ?? result.data.length;
      } else if (Array.isArray(result)) {
        logsData = result;
        totalCount = result.length;
      } else if (result?.logs && Array.isArray(result.logs)) {
        logsData = result.logs;
        totalCount = result.total ?? result.logs.length;
      }

      setLogs(logsData);
      setTotalFromServer(totalCount);
    } catch (err) {
      const errorMsg = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || 'Impossible de charger les logs';
      setError(errorMsg);
      setLogs([]);
      setTotalFromServer(0);
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, filterMethod, filterCode, filterEntite, searchTerm, currentPage, itemsPerPage]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterMethod, filterCode, filterEntite, searchTerm, dateDebut, dateFin]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterMethod !== 'all') params.set('method', filterMethod);
    if (filterCode !== 'all') params.set('code', filterCode);
    if (filterEntite !== 'all') params.set('entite_id', filterEntite);
    if (searchTerm) params.set('search', searchTerm);
    if (dateDebut) params.set('start_date', dateDebut);
    if (dateFin) params.set('end_date', dateFin);
    setSearchParams(params, { replace: true });
  }, [filterMethod, filterCode, filterEntite, searchTerm, dateDebut, dateFin, setSearchParams]);

  // ============================================
  // CHARGEMENT DU DÉTAIL
  // ============================================
  const fetchLogDetail = useCallback(async (logId) => {
    if (!logId) return;

    setDetailLoading(true);
    setDetailError(null);
    setSelectedLog(null);

    try {
      const response = await apiClient.get(`/logs/${logId}`);
      const result = response.data;

      let logDetail = null;
      if (result?.data && typeof result.data === 'object' && !Array.isArray(result.data)) {
        logDetail = result.data;
      } else if (result?.log && typeof result.log === 'object') {
        logDetail = result.log;
      } else if (result && typeof result === 'object' && (result.id || result.method || result.path)) {
        logDetail = result;
      } else if (result?.data?.data && typeof result.data.data === 'object') {
        logDetail = result.data.data;
      } else if (Array.isArray(result) && result.length > 0) {
        logDetail = result[0];
      } else if (Array.isArray(result?.data) && result.data.length > 0) {
        logDetail = result.data[0];
      }

      if (logDetail) {
        setSelectedLog(logDetail);
      } else {
        throw new Error("Format de réponse invalide pour le détail du log");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || 'Impossible de charger les détails du log';
      setDetailError(errorMsg);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetailModal = (log) => {
    setShowDetailModal(true);
    fetchLogDetail(log.id);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
    setDetailError(null);
    setDetailLoading(false);
    setCopiedField(null);
  };

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.max(1, Math.ceil(totalFromServer / itemsPerPage));
  const paginatedLogs = logs;

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => ({
    total: totalFromServer,
    success: logs.filter(l => l.response_code >= 200 && l.response_code < 300).length,
    clientError: logs.filter(l => l.response_code >= 400 && l.response_code < 500).length,
    serverError: logs.filter(l => l.response_code >= 500).length,
  }), [logs, totalFromServer]);

  // ============================================
  // HELPERS
  // ============================================
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).replace(',', '');
    } catch { return '-'; }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'il y a quelques secondes';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return `il y a ${Math.floor(diff / 86400)}j`;
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterMethod("all");
    setFilterCode("all");
    setFilterEntite("all");
    setDateDebut("");
    setDateFin("");
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (filterMethod !== 'all') count++;
    if (filterCode !== 'all') count++;
    if (filterEntite !== 'all') count++;
    if (dateDebut) count++;
    if (dateFin) count++;
    return count;
  }, [searchTerm, filterMethod, filterCode, filterEntite, dateDebut, dateFin]);

  // ✅ Copie avec feedback
  const copyToClipboard = async (data, fieldName = 'content') => {
    try {
      const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Erreur copie:', err);
    }
  };

  // ✅ Formatage JSON avec limite
  const formatJSON = (data) => {
    if (data === null || data === undefined) return 'null';
    try {
      let str;
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          str = JSON.stringify(parsed, null, 2);
        } catch {
          str = data;
        }
      } else {
        str = JSON.stringify(data, null, 2);
      }

      const MAX = 30000;
      if (str.length > MAX) {
        return str.substring(0, MAX) + `\n\n… ⚠️ Contenu tronqué (${(str.length / 1024).toFixed(1)} Ko au total)`;
      }
      return str;
    } catch (e) {
      return String(data);
    }
  };

  // ✅ Info sur la taille
  const getResponseBodyInfo = (data) => {
    if (data === null || data === undefined) return null;
    try {
      const str = typeof data === 'string' ? data : JSON.stringify(data);
      const size = str.length;
      if (size > 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
      if (size > 1024) return `${(size / 1024).toFixed(1)} Ko`;
      return `${size} o`;
    } catch {
      return null;
    }
  };

  const selectedEntiteLabel = useMemo(() => {
    if (filterEntite === 'all') return 'Tous les marchands';
    const entite = entites.find(e => e.value === String(filterEntite));
    return entite ? entite.label : `Entité #${filterEntite}`;
  }, [filterEntite, entites]);

  return (
    <div className="logs-page main main-app p-3 p-lg-4">
      {/* ========== HEADER PREMIUM ========== */}
      <div className="logs-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="logs-header-icon">
              <i className="bi bi-terminal"></i>
            </div>
            <div>
              <h3 className="logs-header-title mb-1">Journal d'activité</h3>
              <p className="logs-header-subtitle mb-0">
                Surveillance en temps réel des requêtes API et événements système
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              className={`logs-btn-filter ${activeFiltersCount > 0 ? 'has-filters' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="bi bi-sliders"></i>
              <span>Filtres</span>
              {activeFiltersCount > 0 && (
                <span className="logs-filter-count">{activeFiltersCount}</span>
              )}
            </Button>
            <Button
              className="logs-btn-refresh"
              onClick={fetchLogs}
              disabled={loading}
            >
              <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
              <span>Actualiser</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== STATS CARDS ========== */}
      <Row className="g-3 mb-4">
        <Col lg={3} md={6}>
          <div className="stat-card stat-card-total">
            <div className="stat-card-bg-icon"><i className="bi bi-activity"></i></div>
            <div className="stat-card-content">
              <div className="stat-card-label">Total requêtes</div>
              <div className="stat-card-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
            <div className="stat-card-footer">
              <i className="bi bi-clock-history me-1"></i>Total sur le serveur
            </div>
          </div>
        </Col>
        <Col lg={3} md={6}>
          <div className="stat-card stat-card-success">
            <div className="stat-card-bg-icon"><i className="bi bi-check-circle"></i></div>
            <div className="stat-card-content">
              <div className="stat-card-label">Succès (page)</div>
              <div className="stat-card-value">{stats.success.toLocaleString('fr-FR')}</div>
            </div>
            <div className="stat-card-footer">
              <i className="bi bi-arrow-up me-1"></i>
              {logs.length > 0 ? Math.round((stats.success / logs.length) * 100) : 0}% de la page
            </div>
          </div>
        </Col>
        <Col lg={3} md={6}>
          <div className="stat-card stat-card-warning">
            <div className="stat-card-bg-icon"><i className="bi bi-exclamation-triangle"></i></div>
            <div className="stat-card-content">
              <div className="stat-card-label">Erreurs client (page)</div>
              <div className="stat-card-value">{stats.clientError.toLocaleString('fr-FR')}</div>
            </div>
            <div className="stat-card-footer">
              <i className="bi bi-info-circle me-1"></i>À surveiller
            </div>
          </div>
        </Col>
        <Col lg={3} md={6}>
          <div className="stat-card stat-card-danger">
            <div className="stat-card-bg-icon"><i className="bi bi-x-octagon"></i></div>
            <div className="stat-card-content">
              <div className="stat-card-label">Erreurs serveur (page)</div>
              <div className="stat-card-value">{stats.serverError.toLocaleString('fr-FR')}</div>
            </div>
            <div className="stat-card-footer">
              <i className="bi bi-lightning me-1"></i>Critiques
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      {showFilters && (
        <div className="filters-panel mb-4">
          <div className="filters-panel-header">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-funnel-fill"></i>
              <span className="fw-bold">Filtres avancés</span>
            </div>
            {activeFiltersCount > 0 && (
              <Button variant="link" size="sm" className="text-decoration-none p-0 text-danger" onClick={resetFilters}>
                <i className="bi bi-x-circle me-1"></i>Effacer tout
              </Button>
            )}
          </div>
          <Row className="g-3">
            <Col lg={3} md={6}>
              <label className="filter-label"><i className="bi bi-search me-1"></i>Recherche</label>
              <InputGroup className="filter-input-group">
                <Form.Control
                  placeholder="Path, IP, action, user ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
                {searchTerm && (
                  <Button variant="link" className="filter-clear-btn" onClick={() => setSearchTerm("")}>
                    <i className="bi bi-x"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>

            <Col lg={3} md={6}>
              <label className="filter-label"><i className="bi bi-building me-1"></i>Marchand</label>
              <Select
                value={
                  filterEntite === 'all'
                    ? { value: 'all', label: 'Tous les marchands' }
                    : entites.find(e => e.value === String(filterEntite))
                      || { value: filterEntite, label: `Entité #${filterEntite}` }
                }
                options={[
                  { value: 'all', label: 'Tous les marchands' },
                  ...entites
                ]}
                onChange={(selected) => setFilterEntite(selected?.value || 'all')}
                placeholder="Sélectionner un marchand..."
                isLoading={loadingEntites}
                isSearchable
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '8px',
                    borderColor: '#e2e8f0',
                    minHeight: '38px',
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    background: '#f8fafc',
                    '&:hover': { borderColor: '#8b5cf6' }
                  }),
                  menu: (base) => ({ ...base, borderRadius: '8px', overflow: 'hidden', zIndex: 100 }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '0.85rem',
                    backgroundColor: state.isSelected ? '#8b5cf6' : state.isFocused ? '#ede9fe' : 'white',
                    color: state.isSelected ? 'white' : '#334155',
                    cursor: 'pointer'
                  }),
                  singleValue: (base) => ({ ...base, color: '#0f172a', fontWeight: 600 }),
                  placeholder: (base) => ({ ...base, color: '#94a3b8' })
                }}
              />
            </Col>

            <Col lg={3} md={6}>
              <label className="filter-label"><i className="bi bi-diagram-3 me-1"></i>Méthode</label>
              <Form.Select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} className="filter-select">
                <option value="all">Toutes</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </Form.Select>
            </Col>
            <Col lg={3} md={6}>
              <label className="filter-label"><i className="bi bi-hash me-1"></i>Code HTTP</label>
              <Form.Select value={filterCode} onChange={(e) => setFilterCode(e.target.value)} className="filter-select">
                <option value="all">Tous</option>
                <option value="200">200 · OK</option>
                <option value="201">201 · Created</option>
                <option value="204">204 · No Content</option>
                <option value="400">400 · Bad Request</option>
                <option value="401">401 · Unauthorized</option>
                <option value="403">403 · Forbidden</option>
                <option value="404">404 · Not Found</option>
                <option value="422">422 · Unprocessable</option>
                <option value="500">500 · Server Error</option>
              </Form.Select>
            </Col>
            <Col lg={1.5} md={6}>
              <label className="filter-label"><i className="bi bi-calendar me-1"></i>Du</label>
              <Form.Control type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="filter-input" />
            </Col>
            <Col lg={1.5} md={6}>
              <label className="filter-label"><i className="bi bi-calendar-check me-1"></i>Au</label>
              <Form.Control type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} className="filter-input" />
            </Col>
          </Row>

          {filterEntite !== 'all' && (
            <div className="filter-active-merchant mt-3">
              <i className="bi bi-building-fill"></i>
              <span>Marchand actif : <strong>{selectedEntiteLabel}</strong></span>
              <button
                className="filter-active-merchant-clear"
                onClick={() => setFilterEntite('all')}
                title="Retirer ce filtre"
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ========== ERROR ALERT ========== */}
      {error && (
        <Alert className="logs-alert logs-alert-danger d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-octagon-fill fs-5"></i>
            <div>
              <div className="fw-bold">Erreur de chargement</div>
              <div className="small opacity-75">{error}</div>
            </div>
          </div>
          <Button className="logs-btn-retry" size="sm" onClick={fetchLogs}>
            <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
          </Button>
        </Alert>
      )}

      {/* ========== TABLE ========== */}
      <div className="logs-table-card">
        <div className="logs-table-toolbar">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="logs-table-count">
              <strong>{totalFromServer.toLocaleString('fr-FR')}</strong>
              <span className="text-muted ms-1">
                {totalFromServer === 1 ? 'entrée' : 'entrées'}
              </span>
            </span>

            {filterEntite !== 'all' && (
              <Badge className="logs-badge-merchant">
                <i className="bi bi-building-fill me-1"></i>
                {selectedEntiteLabel}
              </Badge>
            )}

            {activeFiltersCount > 0 && (
              <Badge className="logs-badge-filtered">
                <i className="bi bi-funnel-fill me-1"></i>
                {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Afficher</span>
            <Form.Select
              size="sm"
              className="logs-per-page-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Form.Select>
            <span className="text-muted small">par page</span>
          </div>
        </div>

        {loading && (
          <div className="logs-loading">
            <div className="logs-loading-spinner"></div>
            <div className="logs-loading-text">Chargement des logs...</div>
          </div>
        )}

        {!loading && (
          <>
            <div className="table-responsive">
              <Table className="logs-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '180px' }}><i className="bi bi-clock me-2"></i>Horodatage</th>
                    <th style={{ width: '100px' }}><i className="bi bi-diagram-3 me-2"></i>Méthode</th>
                    <th><i className="bi bi-signpost-split me-2"></i>Endpoint</th>
                    <th style={{ width: '110px' }}><i className="bi bi-person me-2"></i>User</th>
                    <th style={{ width: '110px' }}><i className="bi bi-building me-2"></i>Marchand</th>
                    <th style={{ width: '140px' }}><i className="bi bi-hdd-network me-2"></i>IP</th>
                    <th style={{ width: '100px' }}><i className="bi bi-shield-check me-2"></i>Statut</th>
                    <th style={{ width: '80px' }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.length > 0 ? (
                    paginatedLogs.map((log, index) => {
                      const methodConf = METHOD_CONFIG[log.method?.toUpperCase()] || METHOD_CONFIG.GET;
                      const statusConf = getStatusConfig(log.response_code);
                      const rowId = log.id || index;

                      const merchantName = entites.find(e => e.value === String(log.entite_id))?.label
                        || (log.entite_id ? `Entité #${log.entite_id}` : '-');

                      return (
                        <tr key={rowId} className="logs-row">
                          <td>
                            <div className="logs-time-cell">
                              <div className="logs-time-main">{formatDate(log.created_at)}</div>
                              <div className="logs-time-ago">{formatTimeAgo(log.created_at)}</div>
                            </div>
                          </td>
                          <td>
                            <span
                              className="logs-method-badge"
                              style={{ color: methodConf.color, backgroundColor: methodConf.bg }}
                            >
                              <i className={`bi ${methodConf.icon}`}></i>
                              {methodConf.label}
                            </span>
                          </td>
                          <td>
                            <div className="logs-path-cell">
                              <code className="logs-path">{log.path || '-'}</code>
                              {log.action && <div className="logs-action-text">{log.action}</div>}
                            </div>
                          </td>
                          <td>
                            <div className="logs-user-cell">
                              <div className="logs-user-avatar"><i className="bi bi-person-fill"></i></div>
                              <span className="logs-user-id">#{log.user_id || '-'}</span>
                            </div>
                          </td>
                          <td>
                            {log.entite_id ? (
                              <span className="logs-merchant-badge">
                                <i className="bi bi-building-fill"></i>
                                {merchantName}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td><code className="logs-ip">{log.ip_address || '-'}</code></td>
                          <td>
                            <span
                              className="logs-status-badge"
                              style={{ color: statusConf.color, backgroundColor: statusConf.bg }}
                            >
                              <i className={`bi ${statusConf.icon}`}></i>
                              {log.response_code}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="logs-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Voir les détails</Tooltip>}>
                                <button className="logs-action-btn" onClick={() => openDetailModal(log)}>
                                  <i className="bi bi-eye"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="logs-empty-state">
                          <div className="logs-empty-icon"><i className="bi bi-inbox"></i></div>
                          <h5 className="logs-empty-title">Aucun log trouvé</h5>
                          <p className="logs-empty-text">
                            {activeFiltersCount > 0
                              ? "Aucune entrée ne correspond à vos critères de recherche"
                              : "Aucun log n'est disponible pour le moment"}
                          </p>
                          {activeFiltersCount > 0 && (
                            <Button className="logs-btn-empty-reset" onClick={resetFilters}>
                              <i className="bi bi-x-circle me-2"></i>Réinitialiser les filtres
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {totalFromServer > 0 && (
              <div className="logs-pagination">
                <div className="logs-pagination-info">
                  Affichage <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> -{' '}
                  <strong>{Math.min(currentPage * itemsPerPage, totalFromServer)}</strong> sur{' '}
                  <strong>{totalFromServer.toLocaleString('fr-FR')}</strong>
                </div>
                <div className="logs-pagination-controls">
                  <button className="logs-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="logs-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <div className="logs-page-indicator">
                    <span className="logs-page-current">{currentPage}</span>
                    <span className="logs-page-sep">/</span>
                    <span className="logs-page-total">{totalPages}</span>
                  </div>
                  <button className="logs-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="logs-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL DÉTAILS ========== */}
      <Modal
        show={showDetailModal}
        onHide={closeDetailModal}
        centered
        size="xl"
        className="logs-modal"
      >
        <div className="logs-modal-header">
          <div className="d-flex align-items-center gap-3">
            <div className="logs-modal-header-icon">
              <i className="bi bi-file-earmark-code"></i>
            </div>
            <div>
              <h5 className="logs-modal-title mb-0">Détails du log</h5>
              <small className="logs-modal-subtitle">
                {selectedLog?.id ? `ID #${selectedLog.id}` : detailLoading ? 'Chargement...' : 'ID —'}
              </small>
            </div>
          </div>
          <button className="logs-modal-close" onClick={closeDetailModal}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="logs-modal-body logs-modal-body-scroll">
          {detailLoading && (
            <div className="logs-detail-loading">
              <div className="logs-loading-spinner"></div>
              <div className="logs-loading-text">Chargement des détails...</div>
            </div>
          )}

          {!detailLoading && detailError && (
            <div className="logs-detail-error">
              <i className="bi bi-exclamation-octagon-fill"></i>
              <h5 className="logs-detail-error-title">Erreur de chargement</h5>
              <p className="logs-detail-error-text">{detailError}</p>
              <button
                className="logs-btn-retry"
                onClick={() => {
                  const logId = selectedLog?.id || logs.find(l => l.id)?.id;
                  if (logId) fetchLogDetail(logId);
                }}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
              </button>
            </div>
          )}

          {!detailLoading && !detailError && selectedLog && (
            <>
              {/* HERO */}
              <div className="logs-detail-hero">
                <div className="logs-detail-hero-status">
                  <span
                    className="logs-status-badge logs-status-badge-lg"
                    style={{
                      color: getStatusConfig(selectedLog.response_code).color,
                      backgroundColor: getStatusConfig(selectedLog.response_code).bg,
                    }}
                  >
                    <i className={`bi ${getStatusConfig(selectedLog.response_code).icon}`}></i>
                    {selectedLog.response_code}
                  </span>
                  <span
                    className="logs-method-badge logs-method-badge-lg"
                    style={{
                      color: METHOD_CONFIG[selectedLog.method?.toUpperCase()]?.color,
                      backgroundColor: METHOD_CONFIG[selectedLog.method?.toUpperCase()]?.bg,
                    }}
                  >
                    <i className={`bi ${METHOD_CONFIG[selectedLog.method?.toUpperCase()]?.icon}`}></i>
                    {selectedLog.method}
                  </span>
                </div>
                <div className="logs-detail-hero-path">
                  <code>{selectedLog.path || '-'}</code>
                </div>
                <div className="logs-detail-hero-time">
                  <i className="bi bi-clock-history"></i>
                  {formatDate(selectedLog.created_at)}
                </div>
              </div>

              {/* GRID */}
              <div className="logs-detail-grid">
                <div className="logs-detail-card">
                  <div className="logs-detail-card-icon logs-detail-card-icon-blue">
                    <i className="bi bi-hash"></i>
                  </div>
                  <div className="logs-detail-card-content">
                    <div className="logs-detail-card-label">ID</div>
                    <div className="logs-detail-card-value">#{selectedLog.id || '-'}</div>
                  </div>
                </div>

                <div className="logs-detail-card">
                  <div className="logs-detail-card-icon logs-detail-card-icon-violet">
                    <i className="bi bi-person"></i>
                  </div>
                  <div className="logs-detail-card-content">
                    <div className="logs-detail-card-label">User ID</div>
                    <div className="logs-detail-card-value">#{selectedLog.user_id || '-'}</div>
                  </div>
                </div>

                <div className="logs-detail-card">
                  <div className="logs-detail-card-icon logs-detail-card-icon-purple">
                    <i className="bi bi-building"></i>
                  </div>
                  <div className="logs-detail-card-content">
                    <div className="logs-detail-card-label">Entité ID</div>
                    <div className="logs-detail-card-value">#{selectedLog.entite_id || '-'}</div>
                  </div>
                </div>

                <div className="logs-detail-card">
                  <div className="logs-detail-card-icon logs-detail-card-icon-cyan">
                    <i className="bi bi-hdd-network"></i>
                  </div>
                  <div className="logs-detail-card-content">
                    <div className="logs-detail-card-label">Adresse IP</div>
                    <div className="logs-detail-card-value font-mono">{selectedLog.ip_address || '-'}</div>
                  </div>
                </div>

                <div className="logs-detail-card">
                  <div className="logs-detail-card-icon logs-detail-card-icon-amber">
                    <i className="bi bi-calendar-check"></i>
                  </div>
                  <div className="logs-detail-card-content">
                    <div className="logs-detail-card-label">Date key</div>
                    <div className="logs-detail-card-value font-mono">{selectedLog.date_key || '-'}</div>
                  </div>
                </div>

                <div className="logs-detail-card">
                  <div className="logs-detail-card-icon logs-detail-card-icon-green">
                    <i className="bi bi-lightning"></i>
                  </div>
                  <div className="logs-detail-card-content">
                    <div className="logs-detail-card-label">Action</div>
                    <div className="logs-detail-card-value text-truncate" title={selectedLog.action}>
                      {selectedLog.action || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* USER AGENT */}
              <div className="logs-detail-useragent">
                <div className="logs-detail-useragent-icon">
                  <i className="bi bi-window"></i>
                </div>
                <div className="logs-detail-useragent-content">
                  <div className="logs-detail-useragent-label">User Agent</div>
                  <div className="logs-detail-useragent-value">
                    {selectedLog.user_agent || '-'}
                  </div>
                </div>
              </div>

              {/* JSON PANELS */}
              <div className="logs-detail-json-grid">
                {/* REQUEST */}
                <div className="logs-json-panel-dark">
                  <div className="logs-json-header">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-box-arrow-in-down"></i>
                      <span className="fw-bold small">Request Body</span>
                    </div>
                    <button
                      className={`logs-copy-icon-btn ${copiedField === 'request' ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(selectedLog.request_body, 'request')}
                      title={copiedField === 'request' ? 'Copié !' : 'Copier'}
                    >
                      <i className={`bi ${copiedField === 'request' ? 'bi-check2' : 'bi-clipboard'}`}></i>
                    </button>
                  </div>
                  <pre className="logs-json-content">
                    {formatJSON(selectedLog.request_body)}
                  </pre>
                </div>

                {/* RESPONSE */}
                <div className="logs-json-panel-dark">
                  <div className="logs-json-header">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-box-arrow-up"></i>
                      <span className="fw-bold small">Response Body</span>
                      {getResponseBodyInfo(selectedLog.response_body) && (
                        <span className="logs-json-size">
                          {getResponseBodyInfo(selectedLog.response_body)}
                        </span>
                      )}
                    </div>
                    <button
                      className={`logs-copy-icon-btn ${copiedField === 'response' ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(selectedLog.response_body, 'response')}
                      title={copiedField === 'response' ? 'Copié !' : 'Copier'}
                    >
                      <i className={`bi ${copiedField === 'response' ? 'bi-check2' : 'bi-clipboard'}`}></i>
                    </button>
                  </div>
                  <pre className="logs-json-content">
                    {formatJSON(selectedLog.response_body)}
                  </pre>
                </div>
              </div>
            </>
          )}

          {!detailLoading && !detailError && !selectedLog && (
            <div className="logs-detail-empty">
              <i className="bi bi-inbox"></i>
              <p>Aucun détail à afficher</p>
            </div>
          )}
        </Modal.Body>

        <div className="logs-modal-footer">
          <Button className="logs-btn-modal-close" onClick={closeDetailModal}>
            Fermer
          </Button>
        </div>
      </Modal>

      {/* ========== CSS PREMIUM ========== */}
      <style>{`
        /* ============ GLOBAL ============ */
        .logs-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .logs-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ============ HEADER ============ */
        .logs-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .logs-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .logs-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .logs-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .logs-btn-filter, .logs-btn-refresh {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: 1px solid #e2e8f0;
          background: white;
          color: #334155;
          transition: all 0.2s;
        }
        .logs-btn-filter:hover, .logs-btn-refresh:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
        }
        .logs-btn-filter.has-filters {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #1d4ed8;
        }
        .logs-btn-refresh {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
        }
        .logs-btn-refresh:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .logs-btn-refresh:disabled { opacity: 0.6; }
        .logs-filter-count {
          background: #3b82f6;
          color: white;
          font-size: 0.7rem;
          padding: 2px 7px;
          border-radius: 10px;
          font-weight: 700;
        }

        /* ============ STATS CARDS ============ */
        .stat-card {
          background: white;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #e2e8f0;
          position: relative;
          overflow: hidden;
          transition: all 0.25s;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px -8px rgba(0,0,0,0.08);
        }
        .stat-card-bg-icon {
          position: absolute;
          top: -20px;
          right: -20px;
          font-size: 100px;
          opacity: 0.04;
        }
        .stat-card-content { position: relative; z-index: 1; }
        .stat-card-label {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }
        .stat-card-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
        }
        .stat-card-footer {
          margin-top: 12px;
          font-size: 0.75rem;
          color: #94a3b8;
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
        }
        .stat-card-total .stat-card-bg-icon { color: #3b82f6; }
        .stat-card-success .stat-card-bg-icon { color: #10b981; }
        .stat-card-warning .stat-card-bg-icon { color: #f59e0b; }
        .stat-card-danger .stat-card-bg-icon { color: #ef4444; }

        /* ============ FILTERS ============ */
        .filters-panel {
          background: white;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #e2e8f0;
          animation: slideDown 0.25s ease;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .filters-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
          color: #0f172a;
        }
        .filter-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 6px;
          display: block;
        }
        .filter-input, .filter-select {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          padding: 8px 12px;
          transition: all 0.2s;
          background: #f8fafc;
        }
        .filter-input:focus, .filter-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          outline: none;
        }
        .filter-input-group { position: relative; }
        .filter-clear-btn {
          position: absolute;
          right: 4px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          padding: 4px 8px;
          z-index: 5;
          text-decoration: none;
        }
        .filter-clear-btn:hover { color: #ef4444; }

        .filter-active-merchant {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
          color: #5b21b6;
          border-radius: 10px;
          border: 1px solid #c4b5fd;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .filter-active-merchant > i { font-size: 1.1rem; }
        .filter-active-merchant strong {
          color: #4c1d95;
          font-weight: 700;
        }
        .filter-active-merchant-clear {
          background: none;
          border: none;
          color: #7c3aed;
          padding: 0;
          margin-left: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.2s;
        }
        .filter-active-merchant-clear:hover {
          color: #5b21b6;
          transform: scale(1.1);
        }

        /* ============ ALERT ============ */
        .logs-alert {
          border-radius: 12px;
          border: none;
          padding: 16px 20px;
        }
        .logs-alert-danger {
          background: #fef2f2;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .logs-btn-retry {
          background: white;
          color: #991b1b;
          border: 1px solid #fecaca;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 6px 14px;
          transition: all 0.2s;
        }
        .logs-btn-retry:hover {
          background: #991b1b;
          color: white;
          border-color: #991b1b;
        }

        /* ============ TABLE CARD ============ */
        .logs-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .logs-table-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .logs-table-count {
          font-size: 0.875rem;
          color: #334155;
        }
        .logs-table-count strong {
          color: #0f172a;
          font-size: 1rem;
        }
        .logs-badge-filtered {
          background: #eff6ff !important;
          color: #1d4ed8 !important;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
        }
        .logs-badge-merchant {
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%) !important;
          color: #5b21b6 !important;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 0.72rem;
          display: inline-flex;
          align-items: center;
        }
        .logs-per-page-select {
          width: auto;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          font-size: 0.8rem;
          padding: 4px 24px 4px 8px;
          background-color: white;
        }

        /* ============ LOADING ============ */
        .logs-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .logs-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .logs-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        /* ============ TABLE ============ */
        .logs-table { font-size: 0.875rem; }
        .logs-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 600;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 14px 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .logs-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .logs-row { transition: background-color 0.15s; }
        .logs-row:hover { background-color: #f8fafc; }

        .logs-time-cell { line-height: 1.3; }
        .logs-time-main {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.8rem;
          font-variant-numeric: tabular-nums;
        }
        .logs-time-ago {
          font-size: 0.7rem;
          color: #94a3b8;
          margin-top: 2px;
        }

        .logs-method-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.7rem;
          letter-spacing: 0.03em;
        }
        .logs-method-badge-lg {
          padding: 8px 14px;
          font-size: 0.85rem;
        }

        .logs-path-cell { line-height: 1.3; }
        .logs-path {
          color: #1e40af;
          background: #eff6ff;
          padding: 3px 8px;
          border-radius: 5px;
          font-size: 0.78rem;
          font-weight: 600;
        }
        .logs-action-text {
          font-size: 0.7rem;
          color: #94a3b8;
          margin-top: 4px;
        }

        .logs-user-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .logs-user-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }
        .logs-user-id {
          font-weight: 600;
          color: #334155;
          font-size: 0.8rem;
        }

        .logs-merchant-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.72rem;
          background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
          color: #7c3aed;
          border: 1px solid #ddd6fe;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .logs-merchant-badge > i {
          font-size: 0.85rem;
          flex-shrink: 0;
        }

        .logs-ip {
          font-size: 0.75rem;
          color: #64748b;
          background: #f1f5f9;
          padding: 3px 8px;
          border-radius: 5px;
        }

        .logs-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.75rem;
          font-variant-numeric: tabular-nums;
        }
        .logs-status-badge-lg {
          padding: 8px 14px;
          font-size: 0.85rem;
        }

        .logs-actions {
          display: inline-flex;
          gap: 6px;
        }
        .logs-action-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
          cursor: pointer;
          font-size: 0.85rem;
        }
        .logs-action-btn:hover {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }

        /* ============ EMPTY STATE ============ */
        .logs-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .logs-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .logs-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .logs-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .logs-btn-empty-reset {
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          transition: all 0.2s;
        }
        .logs-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ============ PAGINATION ============ */
        .logs-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .logs-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
        }
        .logs-pagination-info strong { color: #0f172a; }
        .logs-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .logs-page-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
        }
        .logs-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .logs-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .logs-page-indicator {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0 14px;
          height: 34px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.8rem;
        }
        .logs-page-current { color: #3b82f6; font-weight: 700; }
        .logs-page-sep { color: #cbd5e1; }
        .logs-page-total { color: #64748b; font-weight: 600; }

        /* ============ MODAL ============ */
        .logs-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }
        .logs-modal .modal-dialog {
          max-height: 90vh;
        }
        .logs-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          flex-shrink: 0;
        }
        .logs-modal-header-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          backdrop-filter: blur(10px);
        }
        .logs-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .logs-modal-subtitle {
          color: #94a3b8;
          font-size: 0.8rem;
          font-family: 'SF Mono', Menlo, monospace;
        }
        .logs-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .logs-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .logs-modal-body {
          padding: 24px;
          background: #f8fafc;
        }
        .logs-modal-body-scroll {
          overflow-y: auto;
          flex: 1;
        }
        .logs-modal-body-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .logs-modal-body-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .logs-modal-body-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .logs-modal-body-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .logs-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          flex-shrink: 0;
        }
        .logs-btn-modal-close {
          background: #0f172a;
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .logs-btn-modal-close:hover {
          background: #1e293b;
          color: white;
        }

        /* ============ DETAIL HERO ============ */
        .logs-detail-hero {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 14px;
          margin-bottom: 20px;
          color: white;
          flex-wrap: wrap;
        }
        .logs-detail-hero-status {
          display: flex;
          gap: 10px;
          flex-shrink: 0;
        }
        .logs-detail-hero-path {
          flex: 1;
          min-width: 200px;
        }
        .logs-detail-hero-path code {
          color: #7dd3fc;
          background: rgba(125, 211, 252, 0.1);
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          display: inline-block;
          word-break: break-all;
        }
        .logs-detail-hero-time {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #94a3b8;
          font-size: 0.85rem;
          font-variant-numeric: tabular-nums;
          flex-shrink: 0;
        }
        .logs-detail-hero-time i {
          color: #60a5fa;
          font-size: 1rem;
        }

        /* ============ DETAIL GRID ============ */
        .logs-detail-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .logs-detail-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .logs-detail-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08);
          border-color: #cbd5e1;
        }
        .logs-detail-card-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .logs-detail-card-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .logs-detail-card-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .logs-detail-card-icon-purple { background: #f5f3ff; color: #7c3aed; }
        .logs-detail-card-icon-cyan   { background: #cffafe; color: #0891b2; }
        .logs-detail-card-icon-amber  { background: #fef3c7; color: #d97706; }
        .logs-detail-card-icon-green  { background: #d1fae5; color: #059669; }
        .logs-detail-card-content {
          flex-grow: 1;
          min-width: 0;
        }
        .logs-detail-card-label {
          font-size: 0.68rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .logs-detail-card-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
        }
        .logs-detail-card-value.font-mono {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-size: 0.82rem;
        }

        /* ============ USER AGENT ============ */
        .logs-detail-useragent {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px 20px;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
        }
        .logs-detail-useragent-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #f1f5f9;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .logs-detail-useragent-content {
          flex-grow: 1;
          min-width: 0;
        }
        .logs-detail-useragent-label {
          font-size: 0.7rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .logs-detail-useragent-value {
          font-size: 0.8rem;
          color: #475569;
          word-break: break-all;
          line-height: 1.5;
        }

        /* ============ JSON GRID AMÉLIORÉ ============ */
        .logs-detail-json-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* ============ JSON PANELS COMPACTS ============ */
        .logs-json-panel-dark {
          display: flex;
          flex-direction: column;
          max-height: 320px;
          background: #0f172a;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #1e293b;
        }
        .logs-json-panel-dark .logs-json-header {
          flex-shrink: 0;
          background: #1e293b;
          border-bottom: 1px solid #334155;
          color: #cbd5e1;
          padding: 10px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }
        .logs-json-panel-dark .logs-json-content {
          flex: 1;
          overflow-y: auto;
          margin: 0;
          padding: 14px;
          font-size: 0.75rem;
          font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
          color: #7dd3fc;
          background: #0f172a;
          white-space: pre-wrap;
          word-break: break-word;
          line-height: 1.5;
        }
        .logs-json-panel-dark .logs-json-content::-webkit-scrollbar {
          width: 8px;
        }
        .logs-json-panel-dark .logs-json-content::-webkit-scrollbar-track {
          background: #1e293b;
        }
        .logs-json-panel-dark .logs-json-content::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 10px;
        }
        .logs-json-panel-dark .logs-json-content::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }

        /* ============ COPY ICON BUTTON ============ */
        .logs-copy-icon-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #cbd5e1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.9rem;
          flex-shrink: 0;
        }
        .logs-copy-icon-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          border-color: #3b82f6;
          color: #60a5fa;
          transform: scale(1.05);
        }
        .logs-copy-icon-btn:active {
          transform: scale(0.95);
        }
        .logs-copy-icon-btn.copied {
          background: rgba(16, 185, 129, 0.2);
          border-color: #10b981;
          color: #34d399;
        }

        /* ============ JSON SIZE BADGE ============ */
        .logs-json-size {
          background: rgba(59, 130, 246, 0.15);
          color: #60a5fa;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.65rem;
          font-weight: 700;
          margin-left: 8px;
          flex-shrink: 0;
        }

        /* ============ DETAIL LOADING / ERROR / EMPTY ============ */
        .logs-detail-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .logs-detail-loading .logs-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .logs-detail-error {
          padding: 60px 20px;
          text-align: center;
        }
        .logs-detail-error > i {
          font-size: 3rem;
          color: #ef4444;
          margin-bottom: 16px;
          display: block;
        }
        .logs-detail-error-title {
          color: #991b1b;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .logs-detail-error-text {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 0.875rem;
        }
        .logs-detail-empty {
          padding: 60px 20px;
          text-align: center;
          color: #94a3b8;
        }
        .logs-detail-empty > i {
          font-size: 3rem;
          opacity: 0.5;
          display: block;
          margin-bottom: 12px;
        }
        .logs-detail-empty > p {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 500;
        }

        /* ============ ANIMATIONS ============ */
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 992px) {
          .logs-detail-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .logs-detail-json-grid {
            grid-template-columns: 1fr;
          }
          .logs-json-panel-dark {
            max-height: 280px;
          }
        }
        @media (max-width: 576px) {
          .logs-header { padding: 20px; }
          .logs-header-title { font-size: 1.2rem; }
          .logs-detail-grid { grid-template-columns: 1fr; }
          .logs-detail-hero {
            padding: 16px;
          }
          .logs-detail-hero-status {
            width: 100%;
          }
          .logs-pagination { flex-direction: column; }
        }
      `}</style>

      <Footer />
    </div>
  );
}